// Timeline unificata della scheda cliente: ordini/chiamate/resi/sostituzioni in
// ordine cronologico DESC (GET .../timeline). Fetch propria (non nel detail) con
// abort; ``reloadToken`` la rigioca dopo un merge (che rifonde gli ordini).
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/shared/ui/Card";
import { formatRelative } from "@/shared/format";
import { prettifyApiError } from "@/shared/api";
import {
  IconArrowRight,
  IconFileCatalog,
  IconMail,
  IconPhone,
  IconReturn,
  IconSwap,
} from "@/features/ordini/components/icons";
import { fetchCustomerTimeline, formatDateIt } from "../api";
import type { TimelineItem } from "../types";

// Icona + tinta per categoria. Fallback (IconArrowRight/neutro) per kind che il
// backend potrebbe aggiungere (es. "messaggio", oggi omesso): niente crash.
const KIND_META: Record<string, { Icon: typeof IconPhone; tint: string }> = {
  ordine: { Icon: IconFileCatalog, tint: "text-blue" },
  chiamata: { Icon: IconPhone, tint: "text-teal" },
  reso: { Icon: IconReturn, tint: "text-amber" },
  sostituzione: { Icon: IconSwap, tint: "text-rose-600" },
  messaggio: { Icon: IconMail, tint: "text-ink-2" },
};
const FALLBACK_META = { Icon: IconArrowRight, tint: "text-ink-2" };

function TimelineRow({ item }: { item: TimelineItem }) {
  const { Icon, tint } = KIND_META[item.kind] ?? FALLBACK_META;
  const inner = (
    <>
      <span className={`mt-0.5 shrink-0 ${tint}`} aria-hidden="true">
        <Icon className="h-[15px] w-[15px] fill-none stroke-current stroke-[1.6] [stroke-linecap:round] [stroke-linejoin:round]" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[12.5px] text-ink">{item.title}</span>
        {item.meta && <span className="block truncate text-[11px] text-ink-2">{item.meta}</span>}
      </span>
      <time
        className="shrink-0 text-right text-[11px] tabular-nums text-ink-2"
        title={formatDateIt(item.ts)}
      >
        {formatRelative(item.ts)}
      </time>
    </>
  );

  const rowCls =
    "flex items-start gap-2.5 border-t border-line py-2 first:border-t-0 text-[12.5px]";
  return item.link ? (
    <Link to={item.link} className={`${rowCls} transition-colors hover:bg-paper`}>
      {inner}
    </Link>
  ) : (
    <div className={rowCls}>{inner}</div>
  );
}

export function TimelineSection({
  customerKey,
  reloadToken,
}: {
  customerKey: string;
  reloadToken: number;
}) {
  const [items, setItems] = useState<TimelineItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const ctrl = new AbortController();
    setItems(null);
    setError(null);
    fetchCustomerTimeline(customerKey, ctrl.signal)
      .then((rows) => setItems(rows))
      .catch((err) => {
        if (ctrl.signal.aborted) return;
        setError(prettifyApiError(err));
      });
    return () => ctrl.abort();
  }, [customerKey, reloadToken]);

  return (
    <Card title="Timeline" className="mb-4">
      {error ? (
        <p role="alert" className="text-[12px] text-red">
          {error}
        </p>
      ) : items === null ? (
        <p className="text-[12px] text-ink-2" role="status" aria-live="polite">
          Caricamento timeline…
        </p>
      ) : items.length === 0 ? (
        <p className="text-[12px] text-ink-2">Nessun evento in timeline.</p>
      ) : (
        <div className="flex flex-col">
          {items.map((item, i) => (
            <TimelineRow key={`${item.kind}-${item.ts}-${i}`} item={item} />
          ))}
        </div>
      )}
    </Card>
  );
}
